import { ICellRendererParams } from 'ag-grid-community'
import styled from 'styled-components'
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  MouseEvent as ReactMouseEvent,
} from 'react'
import { UnfoldMoreRounded, UnfoldLessRounded } from '@mui/icons-material'
import { ProgressReportTableContent } from '../table/ProgressReportTable'
import { colorPalette } from '../../../../style/colorPalette'

const EXPANDED_ROW_CLASS = 'has-expanded-comment-cell'

// 展開セルが他行を覆うために使う z-index。AG Grid のヘッダーよりは低い値にする。
const EXPANDED_CELL_Z_INDEX = 10
// ToggleButton と本文の重なりを避けるため ScrollableCell の左 padding を確保
const TOGGLE_BUTTON_SIZE = 20
const TOGGLE_BUTTON_OFFSET = 2
const SCROLLABLE_LEFT_PADDING = TOGGLE_BUTTON_SIZE + 8
const EXPANDED_BORDER_RADIUS = 4
const EXPANDED_BORDER_WIDTH = 1

export type CommentExpansionState = {
  expandedKey: string | null
  setExpandedKey: (key: string | null) => void
}
export const CommentExpansionContext = createContext<CommentExpansionState>({
  expandedKey: null,
  setExpandedKey: () => undefined,
})

type Layout = {
  top: number
  height: number
}

export const CommentCellRenderer = (
  params: ICellRendererParams<ProgressReportTableContent>
) => {
  const { expandedKey, setExpandedKey } = useContext(CommentExpansionContext)
  const [layout, setLayout] = useState<Layout | null>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const scrollableRef = useRef<HTMLDivElement | null>(null)
  const comment = params.data?.comment
  const hasComment = !!comment && comment.trim().length > 0
  const uuid = params.data?.uuid
  const field = params.colDef?.field ?? 'comment'
  const key = uuid ? `${uuid}-${field}` : null
  const expanded = !!key && expandedKey === key
  // colDef.editable は `editable: editing` で設定されているため editing と等価
  const isEditing = params.colDef?.editable === true
  const canToggle = hasComment && !isEditing

  useEffect(() => {
    if (!expanded) return undefined
    const root = rootRef.current
    if (!root) return undefined
    const localRow = root.closest<HTMLElement>('.ag-row')
    const rowIndex = localRow?.getAttribute('row-index')
    const gridRoot = root.closest<HTMLElement>('.ag-root')
    if (!rowIndex || !gridRoot) return undefined
    // pinned-left / center / pinned-right の各コンテナにある同 row-index の .ag-row 全てに class を当てる
    const allRows = gridRoot.querySelectorAll<HTMLElement>(
      `.ag-row[row-index="${rowIndex}"]`
    )
    allRows.forEach(r => r.classList.add(EXPANDED_ROW_CLASS))
    return () => {
      allRows.forEach(r => r.classList.remove(EXPANDED_ROW_CLASS))
    }
  }, [expanded])

  // 展開セルが body 表示エリアからはみ出さないよう、下方向→上方向→内部スクロールの順で配置を決める
  useEffect(() => {
    if (!expanded) {
      setLayout(null)
      return undefined
    }
    const root = rootRef.current
    const inner = scrollableRef.current
    if (!root || !inner) return undefined
    const body = root.closest<HTMLElement>('.ag-body-viewport')
    const cell = root.closest<HTMLElement>('.ag-cell')
    if (!body || !cell) return undefined

    const compute = () => {
      const cellRect = cell.getBoundingClientRect()
      const bodyRect = body.getBoundingClientRect()
      const cellTopInBody = Math.max(0, cellRect.top - bodyRect.top)
      const cellHeight = cellRect.height
      const downwardRoom = bodyRect.height - cellTopInBody
      const upwardRoom = cellTopInBody + cellHeight

      // border-box の CellRoot 高さ。border 分を含めないと内側に余分なスクロールが出る
      const naturalHeight = inner.scrollHeight + EXPANDED_BORDER_WIDTH * 2

      if (naturalHeight <= downwardRoom) {
        // 下方向のみ
        setLayout({ top: 0, height: naturalHeight })
      } else if (naturalHeight <= upwardRoom) {
        // 上方向 (セル下端で揃える)
        setLayout({ top: cellHeight - naturalHeight, height: naturalHeight })
      } else {
        // 両方向とも不足: body 高さで内部スクロール。セル下端を起点に body top で clip
        const finalHeight = Math.min(naturalHeight, bodyRect.height)
        const desiredTop = cellHeight - finalHeight
        const topInBody = cellTopInBody + desiredTop
        const top = topInBody < 0 ? -cellTopInBody : desiredTop
        setLayout({ top, height: finalHeight })
      }
    }
    const id = requestAnimationFrame(compute)
    return () => cancelAnimationFrame(id)
  }, [expanded])

  useEffect(() => {
    if ((!hasComment || isEditing) && expanded) setExpandedKey(null)
  }, [hasComment, isEditing, expanded, setExpandedKey])

  const handleToggle = (e: ReactMouseEvent) => {
    e.stopPropagation()
    if (!key) return
    setExpandedKey(expanded ? null : key)
  }

  return (
    <CellRoot ref={rootRef} $expanded={expanded} $layout={layout}>
      <ScrollableCell ref={scrollableRef}>{comment}</ScrollableCell>
      {canToggle && (
        <ToggleButton type="button" onClick={handleToggle}>
          {expanded ? (
            <UnfoldLessRounded fontSize="small" />
          ) : (
            <UnfoldMoreRounded fontSize="small" />
          )}
        </ToggleButton>
      )}
    </CellRoot>
  )
}

const CellRoot = styled('div')<{
  $expanded: boolean
  $layout: Layout | null
}>(({ $expanded, $layout }) => {
  if (!$expanded) {
    return {
      position: 'relative' as const,
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      overflow: 'hidden' as const,
      display: 'block',
    }
  }

  const top = $layout ? `${$layout.top}px` : '0'
  const height = $layout ? `${$layout.height}px` : 'auto'
  const maxHeight = $layout ? `${$layout.height}px` : '50vh'
  return {
    position: 'absolute' as const,
    top,
    left: 0,
    width: '100%',
    height,
    maxHeight,
    overflow: 'hidden' as const,
    zIndex: EXPANDED_CELL_Z_INDEX,
    background: colorPalette.monotone[0],
    border: `${EXPANDED_BORDER_WIDTH}px solid ${colorPalette.skyBlue[7]}`,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    borderRadius: EXPANDED_BORDER_RADIUS,
    display: 'block',
    boxSizing: 'border-box' as const,
  }
})

const ScrollableCell = styled('div')({
  fontSize: '14px !important',
  color: colorPalette.monotone[9],
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  width: '100%',
  height: '100%',
  boxSizing: 'border-box',
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: `4px 4px 4px ${SCROLLABLE_LEFT_PADDING}px`,
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(0,0,0,0.3) transparent',
  '::-webkit-scrollbar': {
    width: 6,
  },
  '::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '::-webkit-scrollbar-thumb': {
    backgroundColor: 'transparent',
    borderRadius: 3,
  },
  '&:hover::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(0,0,0,0.3)',
  },
})

const ToggleButton = styled('button')({
  position: 'absolute',
  top: TOGGLE_BUTTON_OFFSET,
  left: TOGGLE_BUTTON_OFFSET,
  width: TOGGLE_BUTTON_SIZE,
  height: TOGGLE_BUTTON_SIZE,
  padding: 0,
  border: 'none',
  borderRadius: EXPANDED_BORDER_RADIUS,
  background: 'rgba(255,255,255,0.7)',
  color: colorPalette.monotone[7],
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    background: 'rgba(0,0,0,0.06)',
  },
})
